import { Paging, Table, Tag, Tooltip, Wrapper, useBreadcrumbRoutes, usePaging } from '@maxtropy/components';
import {
  apiV2LoadDispatchCenterConfigFurnaceListPost,
  apiV2LoadDispatchCenterConfigOperationRecordPagePost,
} from '@maxtropy/dmes-apis-v2';
import type { V2LoadDispatchCenterConfigOperationRecordPagePostResponse } from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import styles from './index.module.scss';

type OperationRecord = Exclude<V2LoadDispatchCenterConfigOperationRecordPagePostResponse['list'], undefined>[number];
type ConfigSnapshot = OperationRecord['beforeOperationRecord'];
type RecordPhase = 'before' | 'after';

interface OperationRecordRow {
  key: string;
  phase: RecordPhase;
  source: OperationRecord;
  config?: ConfigSnapshot;
}

const customTypeMap: Record<number, string> = {
  1: '指定炉台',
  2: '优先电石炉',
  3: '优先硅铁炉',
};

const getModeText = (mode: number | undefined, type: 'drop' | 'raise') => {
  if (mode === 1) return type === 'drop' ? '均衡降负荷' : '均衡升负荷';
  if (mode === 2) return type === 'drop' ? '自定义降负荷' : '自定义升负荷';
  return '-';
};

const getCustomTypeText = (mode?: number, customType?: number) =>
  mode === 2 ? customTypeMap[customType ?? 0] ?? '-' : '-';

const RecordValue = ({ value, phase, tooltip }: { value: string; phase: RecordPhase; tooltip?: boolean }) => {
  const content = <span className={phase === 'after' ? styles.afterValue : styles.value}>{value}</span>;
  return tooltip && value !== '-' ? <Tooltip title={value}>{content}</Tooltip> : content;
};

const ConfigOperationRecord = () => {
  const routesContext = useBreadcrumbRoutes();
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;
  const { data: furnaceNameMap = {} } = useRequest(() =>
    apiV2LoadDispatchCenterConfigFurnaceListPost({}).then(response =>
      (response.furnaceList ?? []).reduce<Record<number, string>>((nameMap, item) => {
        if (item.loadObjectId != null) nameMap[item.loadObjectId] = item.deviceName ?? String(item.loadObjectId);
        return nameMap;
      }, {})
    )
  );
  const { data: operationRecords = [], loading } = useRequest(
    () =>
      apiV2LoadDispatchCenterConfigOperationRecordPagePost({
        page: pageOffset,
        size: pageSize,
      }).then(response => {
        setTotalCount(response.total ?? 0);
        return response.list ?? [];
      }),
    { refreshDeps: [pageOffset, pageSize] }
  );
  const dataSource = useMemo<OperationRecordRow[]>(
    () =>
      operationRecords.flatMap((record, index) => {
        const recordKey = record.id ?? `${record.editUser ?? 'record'}-${record.operationTime ?? index}`;
        return [
          { key: `${recordKey}-before`, phase: 'before', source: record, config: record.beforeOperationRecord },
          { key: `${recordKey}-after`, phase: 'after', source: record, config: record.afterOperationRecord },
        ];
      }),
    [operationRecords]
  );

  const getFurnaceNames = (mode?: number, customType?: number, ids?: number[]) => {
    if (mode !== 2 || customType !== 1 || !ids?.length) return '-';
    return ids.map(id => furnaceNameMap[id] ?? String(id)).join('、');
  };

  const renderMergedCell = (row: OperationRecordRow, content: ReactNode) => ({
    children: row.phase === 'before' ? content : null,
    props: { rowSpan: row.phase === 'before' ? 2 : 0 },
  });
  const renderConfigValue = (value: string, row: OperationRecordRow, tooltip = false) => (
    <RecordValue value={value} phase={row.phase} tooltip={tooltip} />
  );
  const columns: ColumnsType<OperationRecordRow> = [
    {
      title: '操作人',
      width: 120,
      render: (_, row) => renderMergedCell(row, row.source.editUser ?? '--'),
    },
    {
      title: '操作时间',
      width: 150,
      render: (_, row) =>
        renderMergedCell(
          row,
          typeof row.source.operationTime === 'number' ? (
            <div className={styles.operationTime}>
              <span>{dayjs(row.source.operationTime).format('YYYY-MM-DD')}</span>
              <span>{dayjs(row.source.operationTime).format('HH:mm:ss')}</span>
            </div>
          ) : (
            '--'
          )
        ),
    },
    {
      title: '修改前后',
      width: 90,
      render: (_, row) => (
        <Tag border="solid" color={row.phase === 'after' ? '#FAAD14' : '#8C8C8C'} className={styles.phaseTag}>
          {row.phase === 'after' ? '修改后' : '修改前'}
        </Tag>
      ),
    },
    {
      title: '调控模式',
      width: 110,
      render: (_, row) => renderConfigValue(getModeText(row.config?.dropMode, 'drop'), row),
    },
    {
      title: '自定义方式',
      width: 110,
      render: (_, row) => renderConfigValue(getCustomTypeText(row.config?.dropMode, row.config?.dropCustomType), row),
    },
    {
      title: '勾选炉台',
      width: 180,
      render: (_, row) =>
        renderConfigValue(
          getFurnaceNames(row.config?.dropMode, row.config?.dropCustomType, row.config?.dropLoadObjectIds),
          row,
          true
        ),
    },
    {
      title: '调控模式',
      width: 110,
      render: (_, row) => renderConfigValue(getModeText(row.config?.raiseMode, 'raise'), row),
    },
    {
      title: '自定义方式',
      width: 110,
      render: (_, row) => renderConfigValue(getCustomTypeText(row.config?.raiseMode, row.config?.raiseCustomType), row),
    },
    {
      title: '勾选炉台',
      width: 180,
      render: (_, row) =>
        renderConfigValue(
          getFurnaceNames(row.config?.raiseMode, row.config?.raiseCustomType, row.config?.raiseLoadObjectIds),
          row,
          true
        ),
    },
    {
      title: '调度建议提前时长',
      width: 150,
      render: (_, row) =>
        renderConfigValue(row.config?.advanceMinutes == null ? '-' : `${row.config.advanceMinutes}min`, row),
    },
  ];

  return (
    <Wrapper routes={[...(routesContext?.routes ?? [])]} hasPadding={false}>
      <main className={styles.page}>
        <Table
          className={styles.table}
          rowKey="key"
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: 1310 }}
        />
        <Paging pagingInfo={pagingInfo} />
      </main>
    </Wrapper>
  );
};

export default ConfigOperationRecord;
